import { useEffect } from "react";
import {
  useLazyGetUrlForMicrosoftLoginQuery,
  useLoginWithMicrosoftMutation,
} from "api";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "hooks/useReduxHooks";
import { setAuth } from "features/authSlice";
import { MicrosoftIcon } from "../images";
import { AppRoutes } from "../../types/enums";
import { openLinkHandler } from "../../utils/openLinkHandler";
import { CircularProgress } from "@mui/material";
import { getErrorMessage } from "../../utils/getErrorMessage";
import { toast } from "react-toastify";
import { CustomToast } from "./CustomToast";
import { GradientButton } from "./GradientButton";

export const LoginWithMicrosoftBtn = () => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const withRedirect = [AppRoutes.PLAYBOOK, AppRoutes.ACCOUNT_PLAN].some(
    (path) => pathname.startsWith(path),
  );

  const [getUrlForMicrosoftLogin, { data }] =
    useLazyGetUrlForMicrosoftLoginQuery();

  const [loginWithMicrosoft, { isLoading, data: loginWithMicrosoftData }] =
    useLoginWithMicrosoftMutation();

  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const state = urlParams.get("state");

    if (code && state) {
      const { csrf_token, redirect_url } = JSON.parse(atob(state || ""));

      if (csrf_token) {
        loginWithMicrosoft({ code, csrf_token })
          .unwrap()
          .then(({ access_token, refresh_token }) => {
            dispatch(
              setAuth({
                accessToken: access_token,
                refreshToken: refresh_token,
              }),
            );
            navigate(redirect_url ?? AppRoutes.CAMPAIGNS);
          })
          .catch((error) => {
            toast.error(
              <CustomToast
                type={"error"}
                text={
                  getErrorMessage(error).globalError ||
                  `Something went wrong. Please try again`
                }
                title={"Failed to Login"}
              />,
            );
            navigate(redirect_url ?? AppRoutes.LOGIN, { replace: true });
          });
      }
    }
  }, []);

  const getUrlForMicrosoftLoginHandler = () => {
    getUrlForMicrosoftLogin({
      redirect_url: withRedirect ? pathname : undefined,
    })
      .unwrap()
      .then(({ url }) => openLinkHandler(url))
      .catch((error) => {
        toast.error(
          <CustomToast
            type={"error"}
            text={
              getErrorMessage(error).globalError ||
              `Something went wrong. Please try again`
            }
            title={"Failed to Login"}
          />,
        );
      });
  };

  return (
    <GradientButton
      onClick={getUrlForMicrosoftLoginHandler}
      fullWidth={true}
      disabled={isLoading || !!loginWithMicrosoftData || !!data}
      startIcon={
        isLoading || !!loginWithMicrosoftData || !!data ? (
          <CircularProgress size={24} sx={{ color: "#1D3E70" }} />
        ) : (
          <MicrosoftIcon color="white" />
        )
      }
      sx={{ height: "48px", borderColor: "#1D3E70" }}
    >
      Sign In with INFUSE Account
    </GradientButton>
  );
};
